import React, { useEffect, useState } from "react";
import axios from "axios";

import { Box, Button, CircularProgress, Typography } from "@mui/material";

import API_BASE_URL from "../../utils/api";
import { useNavigate } from "react-router-dom";

/* =========================================================
   FORMAT CONTRIBUTION TIME

   /user-activity/summary returns totalDurationMinutes,
   so this formatter expects MINUTES.
   ========================================================= */

const formatDuration = (minutes = 0) => {
  const totalMinutes = Math.round(Number(minutes) || 0);

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }

  return `${hours} hr ${remainingMinutes} min`;
};

/* =========================================================
   SEPARATOR
   ========================================================= */

const Separator = () => (
  <Typography
    aria-hidden="true"
    variant="body2"
    sx={{
      color: "#9ca3af",

      display: {
        xs: "none",
        sm: "block",
      },
    }}
  >
    •
  </Typography>
);

/* =========================================================
   MY CONTRIBUTION SUMMARY
   ========================================================= */

const MyContributionSummary = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  /* =======================================================
     LOAD SUMMARY

     IMPORTANT:
     This uses the same endpoint as My Activity so that
     contribution counts stay consistent across the site.
     ======================================================= */

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          setLoading(false);
          return;
        }

        const res = await axios.get(`${API_BASE_URL}/user-activity/summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSummary(res.data);
      } catch (err) {
        console.error("Failed to load contribution summary:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, []);

  /* =======================================================
     LOADING
     ======================================================= */

  if (loading) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          my: 2,
        }}
      >
        <CircularProgress
          size={22}
          sx={{
            color: "#8a6211",
          }}
        />
      </Box>
    );
  }

  /* =======================================================
     NO DATA
     ======================================================= */

  if (!summary) {
    return null;
  }

  /* =======================================================
     UI
     ======================================================= */

  return (
    <Box
      component="section"
      aria-label="Your contribution summary"
      sx={{
        mt: 2,
        mb: 3,

        px: {
          xs: 2,
          sm: 2.5,
          md: 3,
        },

        py: 1.6,

        borderRadius: 2,

        backgroundColor: "#fffaf0",
        border: "1px solid #f3e3b2",

        display: "flex",

        flexDirection: {
          xs: "column",
          sm: "row",
        },

        alignItems: "center",
        justifyContent: "center",

        flexWrap: "wrap",

        gap: {
          xs: 1,
          sm: 2,
        },

        textAlign: "center",
      }}
    >
      {/* ===================================================
          HEADING
         =================================================== */}

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "#6b4f00",

          whiteSpace: {
            xs: "normal",
            sm: "nowrap",
          },
        }}
      >
        Your contribution so far
      </Typography>

      {/* ===================================================
          STATS
         =================================================== */}

      <Box
        sx={{
          display: "flex",

          alignItems: "center",
          justifyContent: "center",

          flexWrap: "wrap",

          gap: {
            xs: 0.7,
            sm: 1,
          },
        }}
      >
        {/* TIME CONTRIBUTED */}

        <Typography
          variant="body2"
          sx={{
            color: "#374151",
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: "#111111",
            }}
          >
            {formatDuration(summary.totalDurationMinutes)}
          </Box>{" "}
          contributed
        </Typography>

        <Separator />

        {/* TRANSCRIBED */}

        <Typography
          variant="body2"
          sx={{
            color: "#374151",
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: "#111111",
            }}
          >
            {summary.transcribedCount || 0}
          </Box>{" "}
          transcribed
        </Typography>

        <Separator />

        {/* REVIEWED */}

        <Typography
          variant="body2"
          sx={{
            color: "#374151",
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: "#111111",
            }}
          >
            {summary.reviewedCount || 0}
          </Box>{" "}
          reviewed
        </Typography>

        {/* =================================================
            VIEW MY ACTIVITY
           ================================================= */}

        <Button
          size="small"
          variant="text"
          onClick={() => navigate("/transcription/my-activity")}
          aria-label="View your activity"
          sx={{
            minHeight: 40,

            px: 1,

            textTransform: "none",

            fontWeight: 700,

            color: "#8a6211",

            borderRadius: 1.5,

            "&:hover": {
              backgroundColor: "#fff7db",
              color: "#6b4f00",
            },

            "&:focus-visible": {
              outline: "3px solid #786d3e",
              outlineOffset: "2px",
            },
          }}
        >
          View activity →
        </Button>
      </Box>
    </Box>
  );
};

export default MyContributionSummary;
